import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Receipt, 
  Barcode, 
  Calendar, 
  Star, 
  StarOff, 
  Clock, 
  CheckCircle, 
  XCircle,
  Search,
  Trash2,
  FileText
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';

interface BillInfo {
  barcode: string;
  digitableLine: string;
  payeeName: string;
  payeeDocument: string;
  amount: number;
  originalAmount: number;
  discount: number;
  interest: number;
  fine: number;
  dueDate: string;
  isExpired: boolean;
  canPayAfterDue: boolean;
  type: 'BOLETO' | 'CONCESSIONARIA' | 'TRIBUTO';
}

interface BillPayment {
  id: string;
  barcode: string;
  payeeName: string;
  amount: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'SCHEDULED';
  scheduledDate?: string;
  paidAt?: string;
  createdAt: string;
}

interface FavoriteBiller {
  id: string;
  name: string;
  document: string;
  lastBarcode?: string;
  lastAmount?: number;
  createdAt: string;
}

export default function BillPayment() {
  const queryClient = useQueryClient();
  const [barcodeInput, setBarcodeInput] = useState('');
  const [billInfo, setBillInfo] = useState<BillInfo | null>(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [activeTab, setActiveTab] = useState<'pay' | 'history' | 'favorites'>('pay');

  // Fetch payment history
  const { data: history, isLoading: loadingHistory } = useQuery({
    queryKey: ['bill-payments-history'],
    queryFn: async () => {
      const response = await api.get('/banking/bills/history');
      return response.data as BillPayment[];
    },
    enabled: activeTab === 'history',
  });

  // Fetch favorite billers
  const { data: favorites, isLoading: loadingFavorites } = useQuery({
    queryKey: ['favorite-billers'],
    queryFn: async () => {
      const response = await api.get('/banking/bills/favorites');
      return response.data as FavoriteBiller[];
    },
    enabled: activeTab === 'favorites',
  });

  // Decode barcode mutation
  const decodeMutation = useMutation({
    mutationFn: async (barcode: string) => {
      const response = await api.post('/banking/bills/decode', { barcode });
      return response.data as BillInfo;
    },
    onSuccess: (data) => {
      setBillInfo(data);
    },
  });

  // Pay bill mutation
  const payMutation = useMutation({
    mutationFn: async (data: { barcode: string; amount: number }) => {
      const response = await api.post('/banking/bills/pay', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill-payments-history'] });
      setShowPayModal(false);
      setBillInfo(null);
      setBarcodeInput('');
    },
  });

  // Schedule payment mutation
  const scheduleMutation = useMutation({
    mutationFn: async (data: { barcode: string; amount: number; scheduledDate: string }) => {
      const response = await api.post('/banking/bills/schedule', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill-payments-history'] });
      setShowScheduleModal(false);
      setBillInfo(null);
      setBarcodeInput('');
      setScheduleDate('');
    },
  });

  // Save favorite mutation
  const saveFavoriteMutation = useMutation({
    mutationFn: async (data: { name: string; document: string; barcode?: string }) => {
      const response = await api.post('/banking/bills/favorites', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-billers'] });
    },
  });

  // Remove favorite mutation
  const removeFavoriteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/banking/bills/favorites/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-billers'] });
    },
  });

  const handleDecode = () => {
    if (barcodeInput.length >= 44) {
      decodeMutation.mutate(barcodeInput.replace(/\D/g, ''));
    }
  };

  const handlePay = () => {
    if (billInfo) {
      payMutation.mutate({ barcode: billInfo.barcode, amount: billInfo.amount });
    }
  };

  const handleSchedule = () => {
    if (billInfo && scheduleDate) {
      scheduleMutation.mutate({ 
        barcode: billInfo.barcode, 
        amount: billInfo.amount,
        scheduledDate: scheduleDate 
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
      COMPLETED: 'success',
      PENDING: 'warning',
      PROCESSING: 'info',
      SCHEDULED: 'info',
      FAILED: 'error',
    };
    const labels: Record<string, string> = {
      COMPLETED: 'Pago',
      PENDING: 'Pendente',
      PROCESSING: 'Processando',
      SCHEDULED: 'Agendado',
      FAILED: 'Falhou',
    };
    return <Badge variant={variants[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  return (
    <PageContainer
      title="Pagamento de Contas"
      description="Pague boletos e contas de consumo"
      icon={Receipt}
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'pay' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('pay')}
        >
          <Barcode className="w-4 h-4 mr-2" />
          Pagar Conta
        </Button>
        <Button
          variant={activeTab === 'history' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('history')}
        >
          <Clock className="w-4 h-4 mr-2" />
          Histórico
        </Button>
        <Button
          variant={activeTab === 'favorites' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('favorites')}
        >
          <Star className="w-4 h-4 mr-2" />
          Favoritos
        </Button>
      </div>

      {/* Pay Tab */}
      {activeTab === 'pay' && (
        <div className="space-y-6">
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-surface-100 mb-4">
                Digite o código de barras ou linha digitável
              </h3>
              <div className="flex gap-4">
                <Input
                  placeholder="Código de barras ou linha digitável"
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  onClick={handleDecode}
                  disabled={barcodeInput.length < 44 || decodeMutation.isPending}
                >
                  {decodeMutation.isPending ? 'Consultando...' : 'Consultar'}
                </Button>
              </div>
              {decodeMutation.isError && (
                <p className="text-error mt-2 text-sm">
                  Erro ao consultar boleto. Verifique o código e tente novamente.
                </p>
              )}
            </div>
          </Card>

          {/* Bill Info */}
          {billInfo && (
            <Card>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-surface-100">
                    Dados do Boleto
                  </h3>
                  {billInfo.isExpired && (
                    <Badge variant="error">Vencido</Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-surface-400">Beneficiário</p>
                    <p className="text-surface-100 font-medium">{billInfo.payeeName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-surface-400">CNPJ/CPF</p>
                    <p className="text-surface-100">{billInfo.payeeDocument}</p>
                  </div>
                  <div>
                    <p className="text-sm text-surface-400">Vencimento</p>
                    <p className="text-surface-100">{formatDate(billInfo.dueDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-surface-400">Tipo</p>
                    <p className="text-surface-100">{billInfo.type}</p>
                  </div>
                </div>

                <div className="border-t border-surface-700 pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-surface-400">Valor Original</span>
                    <span className="text-surface-100">{formatCurrency(billInfo.originalAmount)}</span>
                  </div>
                  {billInfo.discount > 0 && (
                    <div className="flex justify-between text-success">
                      <span>Desconto</span>
                      <span>- {formatCurrency(billInfo.discount)}</span>
                    </div>
                  )}
                  {billInfo.interest > 0 && (
                    <div className="flex justify-between text-error">
                      <span>Juros</span>
                      <span>+ {formatCurrency(billInfo.interest)}</span>
                    </div>
                  )}
                  {billInfo.fine > 0 && (
                    <div className="flex justify-between text-error">
                      <span>Multa</span>
                      <span>+ {formatCurrency(billInfo.fine)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold border-t border-surface-700 pt-2">
                    <span className="text-surface-100">Total a Pagar</span>
                    <span className="text-primary-400">{formatCurrency(billInfo.amount)}</span>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button onClick={() => setShowPayModal(true)} className="flex-1">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Pagar Agora
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={() => setShowScheduleModal(true)}
                    className="flex-1"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Agendar
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => saveFavoriteMutation.mutate({
                      name: billInfo.payeeName,
                      document: billInfo.payeeDocument,
                      barcode: billInfo.barcode,
                    })}
                  >
                    <Star className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">
              Histórico de Pagamentos
            </h3>
            {loadingHistory ? (
              <div className="text-center py-8 text-surface-400">Carregando...</div>
            ) : history && history.length > 0 ? (
              <div className="space-y-4">
                {history.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-4 bg-surface-800 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-surface-100 font-medium">{payment.payeeName}</p>
                        <p className="text-sm text-surface-400">
                          {payment.scheduledDate 
                            ? `Agendado: ${formatDate(payment.scheduledDate)}`
                            : formatDate(payment.createdAt)
                          }
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-surface-100 font-medium">
                        {formatCurrency(payment.amount)}
                      </p>
                      {getStatusBadge(payment.status)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-surface-400">
                Nenhum pagamento encontrado
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Favorites Tab */}
      {activeTab === 'favorites' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">
              Favoritos
            </h3>
            {loadingFavorites ? (
              <div className="text-center py-8 text-surface-400">Carregando...</div>
            ) : favorites && favorites.length > 0 ? (
              <div className="space-y-4">
                {favorites.map((fav) => (
                  <div
                    key={fav.id}
                    className="flex items-center justify-between p-4 bg-surface-800 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <Star className="w-5 h-5 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-surface-100 font-medium">{fav.name}</p>
                        <p className="text-sm text-surface-400">{fav.document}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {fav.lastBarcode && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setBarcodeInput(fav.lastBarcode!);
                            setActiveTab('pay');
                            decodeMutation.mutate(fav.lastBarcode!);
                          }}
                        >
                          <Search className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFavoriteMutation.mutate(fav.id)}
                      >
                        <Trash2 className="w-4 h-4 text-error" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-surface-400">
                Nenhum favorito salvo
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Pay Confirmation Modal */}
      <Modal
        isOpen={showPayModal}
        onClose={() => setShowPayModal(false)}
        title="Confirmar Pagamento"
      >
        <div className="space-y-4">
          <p className="text-surface-300">
            Confirma o pagamento de <strong className="text-primary-400">
              {billInfo && formatCurrency(billInfo.amount)}
            </strong> para <strong>{billInfo?.payeeName}</strong>?
          </p>
          <div className="flex gap-4">
            <Button
              variant="secondary"
              onClick={() => setShowPayModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handlePay}
              disabled={payMutation.isPending}
              className="flex-1"
            >
              {payMutation.isPending ? 'Processando...' : 'Confirmar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Schedule Modal */}
      <Modal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title="Agendar Pagamento"
      >
        <div className="space-y-4">
          <Input
            type="date"
            label="Data do Pagamento"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
          />
          <p className="text-surface-300">
            Valor: <strong className="text-primary-400">
              {billInfo && formatCurrency(billInfo.amount)}
            </strong>
          </p>
          <div className="flex gap-4">
            <Button
              variant="secondary"
              onClick={() => setShowScheduleModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSchedule}
              disabled={!scheduleDate || scheduleMutation.isPending}
              className="flex-1"
            >
              {scheduleMutation.isPending ? 'Agendando...' : 'Agendar'}
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}
